import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import AdminDashboard from '../views/admin/AdminDashboard.vue';
import CompanyDashboard from '../views/company/CompanyDashboard.vue';
import Login from '../views/auth/Login.vue';
import Register from '../views/auth/Register.vue';
import TestsList from '../views/tests/TestsList.vue';
import TestCreate from '../views/tests/TestCreate.vue';
import TestEdit from '../views/tests/TestEdit.vue';
import TestPreview from '../views/tests/TestPreview.vue';
import TestExecute from '../views/tests/TestExecute.vue';
import EntriesList from '../views/entries/EntriesList.vue';
import EntryDetail from '../views/entries/EntryDetail.vue';
import CompanySettings from '../views/company/CompanySettings.vue';
import CompanyList from '../views/company/CompanyList.vue';
import Profile from '../views/Profile.vue';
import NotFound from '../views/NotFound.vue';
import CompanyCreate from '../views/companies/CompanyCreate.vue';
import CompanyEdit from '../views/companies/CompanyEdit.vue';
import CompaniesList from '../views/companies/CompaniesList.vue';
import TestTake from '../views/public/TestTake.vue';
import AdminAnalysis from '@/views/admin/AdminAnalysis.vue';
import ApplicantsList from '../views/applicants/ApplicantsList.vue';
import ApplicantDetail from '../views/applicants/ApplicantDetail.vue';
import QuestionGroups from '../views/tests/QuestionGroups.vue';
import QuestionGroupDetails from '../views/tests/QuestionGroupDetails.vue';

const routes = [
  {
    path: '/',
    name: 'home',
    beforeEnter: (to, from, next) => {
      const userData = localStorage.getItem('user');
      let user = null;
      
      try {
        user = JSON.parse(userData);
      } catch (e) {
        console.error('Fehler beim Parsen der Benutzerdaten:', e);
      }
      
      console.log('Root-Route Navigation für Benutzer:', user?.name);
      
      if (user && user.role === 'super_admin') {
        next('/admin/dashboard');
      } else if (user) {
        next('/company/dashboard');
      } else {
        next('/login');
      }
    },
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      public: true,
      onlyWhenLoggedOut: true,
      hideFooter: true
    }
  },
  {
    path: '/register',
    name: 'register',
    component: Register,
    meta: {
      public: true,
      onlyWhenLoggedOut: true
    }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    redirect: to => {
      // Immer den Store verwenden, um den aktuellen Benutzer zu erhalten
      const authStore = useAuthStore();
      const user = authStore.user;
      
      console.log('Dashboard-Redirect für User:', user);
      
      // Klare Unterscheidung: Superadmins -> Admin-Dashboard, alle anderen -> Company-Dashboard
      if (user && user.role === 'super_admin') {
        console.log('Leite Superadmin zum Admin-Dashboard weiter');
        return '/admin/dashboard';
      } else if (user) {
        console.log('Leite normalen Benutzer zum Company-Dashboard weiter');
        return '/company/dashboard';
      } else {
        // Fallback, falls noch kein Benutzer geladen ist
        console.log('Kein Benutzer gefunden, leite zur Startseite weiter');
        return '/login';
      }
    },
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/company/dashboard',
    name: 'company-dashboard',
    component: CompanyDashboard,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/profile',
    name: 'profile',
    component: Profile,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/tests',
    name: 'TestsList',
    component: TestsList,
    meta: { 
      requiresAuth: true 
    }
  },
  {
    path: '/tests/create',
    name: 'tests.create',
    component: TestCreate,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/tests/:id/edit',
    name: 'tests.edit',
    component: TestEdit,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/tests/:id/preview',
    name: 'PreviewTest',
    component: TestPreview,
    meta: { requiresAuth: true }
  },
  {
    path: '/test/:hash',
    name: 'ExecuteTest',
    component: TestExecute,
    meta: { public: true }
  },
  {
    path: '/applicants',
    name: 'applicants',
    component: () => import('../views/applicants/ApplicantsList.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/applicants/:id',
    name: 'applicant-detail',
    component: () => import('../views/applicants/ApplicantDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/entries',
    name: 'Entries',
    component: EntriesList,
    meta: { requiresAuth: true }
  },
  {
    path: '/entries/:id',
    name: 'EntryDetail',
    component: EntryDetail,
    meta: { requiresAuth: true }
  },
  {
    path: '/settings',
    name: 'CompanySettings',
    component: CompanySettings,
    meta: { 
      requiresAuth: true,
      allowSuperAdmin: true
    }
  },
  {
    path: '/companies',
    name: 'companies',
    component: CompaniesList,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/companies/create',
    name: 'companies.create',
    component: CompanyCreate,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/companies/:id/edit',
    name: 'companies.edit',
    component: CompanyEdit,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/test/:id',
    name: 'test.take',
    component: TestTake,
    meta: {
      public: true
    }
  },
  {
    path: '/question-groups',
    name: 'question-groups',
    component: QuestionGroups,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/question-groups/:id',
    name: 'question-groups.details',
    component: QuestionGroupDetails,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/admin/dashboard',
    component: AdminDashboard
  },
  {
    path: '/analysis',
    name: 'analysis',
    component: AdminAnalysis,
    meta: {
      requiresAuth: true,
      superAdminOnly: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: 'active'
});

// Navigation Guard zur korrekten Überprüfung der Authentifizierung
router.beforeEach(async (to, from, next) => {
  // Prüfen, ob die Route öffentlich zugänglich ist
  const isPublic = to.matched.some(record => record.meta.public);
  const onlyWhenLoggedOut = to.matched.some(record => record.meta.onlyWhenLoggedOut);
  const requiresAuth = !isPublic;
  
  // Debugging
  console.log(`Router Navigation: ${from.path} -> ${to.path}`);
  console.log(`Route Eigenschaften: public=${isPublic}, onlyWhenLoggedOut=${onlyWhenLoggedOut}, requiresAuth=${requiresAuth}`);
  
  // Authentifizierungsprüfung
  const authStore = useAuthStore();
  console.log(`Auth Status: isAuthenticated=${authStore.isAuthenticated}, Token=${!!authStore.token}, User=${!!authStore.user}`);
  
  if (requiresAuth) {
    try {
      // Auf Authentifizierung prüfen
      const isAuthenticated = authStore.isAuthenticated;
      
      if (!isAuthenticated) {
        // Wenn nicht authentifiziert, zum Login weiterleiten
        console.log('Nicht authentifiziert, leite zum Login weiter');
        return next('/login');
      }
      
      // Prüfen, ob es eine super_admin-Einschränkung gibt
      const superAdminOnly = to.matched.some(record => record.meta.superAdminOnly);
      const isSuperAdmin = authStore.user && authStore.user.role === 'super_admin';
      
      if (superAdminOnly && !isSuperAdmin) {
        console.warn('Diese Seite benötigt Super-Admin-Berechtigungen');
        return next('/dashboard');
      }
      
      return next();
    } catch (error) {
      console.error('Fehler bei der Authentifizierungsprüfung:', error);
      return next('/login');
    }
  } else if (isPublic && onlyWhenLoggedOut && authStore.isAuthenticated) {
    // Wenn der Benutzer bereits angemeldet ist und versucht, auf eine Route
    // zuzugreifen, die nur für nicht angemeldete Benutzer bestimmt ist (z.B. Login)
    return next('/dashboard');
  }
  
  return next();
});

export default router; 